/**
 * MassenAnschreibenModal — Batch-Anschreiben für ausgewählte Forderungen
 *
 * Zeigt eine Zusammenfassung der ausgewählten Forderungen gruppiert nach Marke/Typ,
 * sendet den Batch-Request an /api/forderung-batch-anschreiben und zeigt
 * Fortschritt + Ergebnis.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertTriangle, CheckCircle2, Loader2, Mail, Send, XCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import type { Forderungsakte } from '@/hooks/useForderungen';

// ─── Types ────────────────────────────────────────────────────────────────────
interface BatchResult {
  forderungsakte_id: string;
  status: 'success' | 'skipped' | 'error';
  draft_id?: string;
  new_stage?: number;
  reason?: string;
}

interface BatchResponse {
  ok: boolean;
  results: BatchResult[];
  summary: { total: number; success: number; skipped: number; failed: number };
}

interface MassenAnschreibenModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedForderungen: Forderungsakte[];
  onComplete?: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatCurrency(n: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
}

function getTypLabel(typ: string): string {
  switch (typ) {
    case 'mietfahrzeug': return 'Mietwagen';
    case 'autoglas': return 'Autoglas';
    case 'werkstatt_auslage': return 'Werkstatt';
    default: return typ;
  }
}

function getTypBrand(typ: string): { name: string; email: string; color: string } {
  switch (typ) {
    case 'mietfahrzeug':
      return { name: 'MietGo', email: 'info@mietgo.de', color: 'bg-blue-100 text-blue-700' };
    case 'autoglas':
      return { name: 'Lenneglas', email: 'info@lenneglas.de', color: 'bg-green-100 text-green-700' };
    case 'werkstatt_auslage':
      return { name: 'Gutachter Koop', email: 'info@gutachter-koop.de', color: 'bg-slate-100 text-slate-700' };
    default:
      return { name: 'Gutachter Koop', email: 'info@gutachter-koop.de', color: 'bg-slate-100 text-slate-700' };
  }
}

function getStageName(stage: number): string {
  const names: Record<number, string> = {
    0: 'Neu',
    1: 'Zahlungserinnerung',
    2: '1. Mahnung',
    3: '2. Mahnung',
    4: '3. Mahnung',
    5: 'Letzte Mahnung',
    6: 'Anwalt',
    7: 'Klage',
    8: 'Vollstreckung',
    9: 'Erledigt',
  };
  return names[stage] ?? `Stage ${stage}`;
}

// ─── Component ────────────────────────────────────────────────────────────────
export function MassenAnschreibenModal({
  open,
  onOpenChange,
  selectedForderungen,
  onComplete,
}: MassenAnschreibenModalProps) {
  const { session } = useAuth();
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<BatchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Group by typ for overview
  const grouped = useMemo(() => {
    const groups: Record<string, { brand: ReturnType<typeof getTypBrand>; items: Forderungsakte[] }> = {};
    for (const f of selectedForderungen) {
      const key = f.typ || 'mietfahrzeug';
      if (!groups[key]) {
        groups[key] = { brand: getTypBrand(key), items: [] };
      }
      groups[key].items.push(f);
    }
    return groups;
  }, [selectedForderungen]);

  // Validation
  const validation = useMemo(() => {
    const sendable: Forderungsakte[] = [];
    const noEmail: Forderungsakte[] = [];
    const tooHighStage: Forderungsakte[] = [];

    for (const f of selectedForderungen) {
      if (!f.versicherung_email) {
        noEmail.push(f);
      } else if ((f.stage ?? 0) > 5) {
        tooHighStage.push(f);
      } else {
        sendable.push(f);
      }
    }
    return { sendable, noEmail, tooHighStage };
  }, [selectedForderungen]);

  const totalBetrag = useMemo(
    () => selectedForderungen.reduce((sum, f) => sum + (f.gesamtbetrag || 0), 0),
    [selectedForderungen],
  );

  const handleSend = useCallback(async () => {
    setSending(true);
    setError(null);
    setResult(null);

    try {
      const ids = validation.sendable.map(f => f.id);
      if (ids.length === 0) {
        setError('Keine versandfähigen Forderungen ausgewählt.');
        return;
      }

      const resp = await fetch('/api/forderung-batch-anschreiben', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ forderungsakte_ids: ids }),
      });

      const json = await resp.json();
      if (!resp.ok) throw new Error(json.error ?? `HTTP ${resp.status}`);

      setResult(json as BatchResponse);
      onComplete?.();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler');
    } finally {
      setSending(false);
    }
  }, [validation.sendable, session, onComplete]);

  const handleClose = () => {
    if (!sending) {
      setResult(null);
      setError(null);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Massen-Anschreiben
          </DialogTitle>
          <DialogDescription>
            {selectedForderungen.length} Forderungen ausgewählt — Mahnschreiben als E-Mail-Drafts erstellen
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 max-h-[50vh] pr-2">
          {/* Result view */}
          {result && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-semibold">Batch abgeschlossen</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-green-50 rounded-lg p-2">
                  <div className="text-lg font-bold text-green-700">{result.summary.success}</div>
                  <div className="text-xs text-green-600">Erstellt</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-2">
                  <div className="text-lg font-bold text-amber-700">{result.summary.skipped}</div>
                  <div className="text-xs text-amber-600">Übersprungen</div>
                </div>
                <div className="bg-red-50 rounded-lg p-2">
                  <div className="text-lg font-bold text-red-700">{result.summary.failed}</div>
                  <div className="text-xs text-red-600">Fehler</div>
                </div>
              </div>

              {/* Show skipped/failed details */}
              {result.results.filter(r => r.status !== 'success').length > 0 && (
                <div className="space-y-1 mt-2">
                  <div className="text-xs font-medium text-gray-500">Details:</div>
                  {result.results
                    .filter(r => r.status !== 'success')
                    .slice(0, 10)
                    .map(r => {
                      const f = selectedForderungen.find(x => x.id === r.forderungsakte_id);
                      return (
                        <div key={r.forderungsakte_id} className="flex items-center gap-2 text-xs py-1 border-b border-gray-100">
                          {r.status === 'skipped' ? (
                            <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />
                          ) : (
                            <XCircle className="w-3 h-3 text-red-500 flex-shrink-0" />
                          )}
                          <span className="truncate flex-1">
                            {f?.versicherung_gegner || f?.akten_nr || r.forderungsakte_id.slice(0, 8)}
                          </span>
                          <span className="text-gray-400 flex-shrink-0">{r.reason}</span>
                        </div>
                      );
                    })}
                </div>
              )}

              <p className="text-xs text-gray-500 mt-3">
                Die E-Mail-Drafts wurden erstellt und warten auf Freigabe im Kommunikations-Bereich.
              </p>
            </div>
          )}

          {/* Sending view */}
          {sending && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <div className="text-sm font-medium">Erstelle Mahnschreiben...</div>
              <Progress value={50} className="w-full" />
              <div className="text-xs text-gray-500">
                {validation.sendable.length} Anschreiben werden verarbeitet
              </div>
            </div>
          )}

          {/* Preview / confirmation view */}
          {!result && !sending && (
            <div className="space-y-4">
              {/* Summary cards per brand */}
              {Object.entries(grouped).map(([typ, { brand, items }]) => (
                <div key={typ} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className={brand.color}>
                        {brand.name}
                      </Badge>
                      <span className="text-sm font-medium">{getTypLabel(typ)}</span>
                    </div>
                    <span className="text-xs text-gray-500">{items.length} Forderungen</span>
                  </div>
                  <div className="text-xs text-gray-600">
                    Absender: <span className="font-mono">{brand.email}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {Array.from(new Set(items.map(i => i.stage ?? 0))).sort().map(stage => {
                      const count = items.filter(i => (i.stage ?? 0) === stage).length;
                      return (
                        <Badge key={stage} variant="outline" className="text-[10px]">
                          {getStageName(stage)} ({count})
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Total */}
              <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                <span className="text-sm font-medium">Gesamtforderung</span>
                <span className="text-sm font-bold">{formatCurrency(totalBetrag)}</span>
              </div>

              {/* Warnings */}
              {validation.noEmail.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs font-medium text-amber-800">
                      {validation.noEmail.length} ohne E-Mail-Adresse
                    </div>
                    <div className="text-xs text-amber-600 mt-0.5">
                      Diese werden übersprungen (keine Versicherungs-E-Mail hinterlegt)
                    </div>
                  </div>
                </div>
              )}

              {validation.tooHighStage.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs font-medium text-amber-800">
                      {validation.tooHighStage.length} in Anwalt/Klage-Stage
                    </div>
                    <div className="text-xs text-amber-600 mt-0.5">
                      Stages 6–8 werden übersprungen (kein automatischer Versand)
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-red-700">{error}</div>
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="flex-shrink-0">
          {result ? (
            <Button onClick={handleClose}>Schließen</Button>
          ) : (
            <div className="flex gap-2 w-full justify-end">
              <Button variant="outline" onClick={handleClose} disabled={sending}>
                Abbrechen
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || validation.sendable.length === 0}
                className="gap-1"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {validation.sendable.length} Anschreiben erstellen
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
